import React from "react";

import { Schedule } from "shared/types/Schedule";
import { IOdont } from "shared/types/SendSchedule";

interface IProps {
  children: any;
}

export interface IAvaliationContext {
  step: number;
  setStep: (value: number) => void;
  patientObj: Schedule;
  firstQuestion: string;
  setFirstQuestion: (value: string) => void;
  firstReason: string;
  setFirstReason: (value: string) => void;
  secondQuestion: string;
  setSecondQuestion: (value: string) => void;
  care: boolean;
  setCare: (value: boolean) => void;
  careReason: string;
  setCareReason: (value: string) => void;
  careReasonError: string;
  setCareReasonError: (value: string) => void;
  periodont: boolean;
  setPeriodont: (value: boolean) => void;
  periodontReason: string;
  setPeriodontReason: (value: string) => void;
  periodontReasonError: string;
  setPeriodontReasonError: (value: string) => void;
  cirurgy: boolean;
  setCirurgy: (value: boolean) => void;
  cirurgyReason: string;
  setCirurgyReason: (value: string) => void;
  cirurgyReasonError: string;
  setCirurgyReasonError: (value: string) => void;
  description: string;
  setDescription: (value: string) => void;
  descriptionError: string;
  setDescriptionError: (value: string) => void;

  odontogramaCarie: IOdont[];
  setOdontogramaCarie: (value: (prev: IOdont[]) => IOdont[]) => void;

  odontogramaProblem: IOdont[];
  setOdontogramaProblem: (value: (prev: IOdont[]) => IOdont[]) => void;

  odontogramaCirurgy: IOdont[];
  setOdontogramaCirurgy: (value: (prev: IOdont[]) => IOdont[]) => void;

  reason: string;
  setReason: (value: string) => void;
  reasonError: string;
  setReasonError: (value: string) => void;
  clearAvaliationData: () => void;
}

const AVALIATION_STORAGE_KEY = "avaliationFormData";

// Função para obter dados salvos do localStorage
const getSavedData = () => {
  try {
    const saved = localStorage.getItem(AVALIATION_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Erro ao recuperar dados da avaliação:", e);
  }
  return null;
};

const AvaliationContext = React.createContext({} as IAvaliationContext);

const AvaliationProvider: React.FC<IProps> = ({ children }) => {
  // Carregar dados salvos na inicialização
  const savedData = getSavedData();

  const [step, setStep] = React.useState<number>(savedData?.step || 1);
  const [firstQuestion, setFirstQuestion] = React.useState<string>(savedData?.firstQuestion || "");
  const [firstReason, setFirstReason] = React.useState<string>(savedData?.firstReason || "");
  const [secondQuestion, setSecondQuestion] = React.useState<string>(savedData?.secondQuestion || "");

  const [care, setCare] = React.useState<boolean>(savedData?.care || false);
  const [careReason, setCareReason] = React.useState<string>(savedData?.careReason || "");
  const [careReasonError, setCareReasonError] = React.useState<string>("");

  const [periodont, setPeriodont] = React.useState<boolean>(savedData?.periodont || false);
  const [periodontReason, setPeriodontReason] = React.useState<string>(savedData?.periodontReason || "");
  const [periodontReasonError, setPeriodontReasonError] =
    React.useState<string>("");

  const [cirurgy, setCirurgy] = React.useState<boolean>(savedData?.cirurgy || false);
  const [cirurgyReason, setCirurgyReason] = React.useState<string>(savedData?.cirurgyReason || "");
  const [cirurgyReasonError, setCirurgyReasonError] =
    React.useState<string>("");

  const [description, setDescription] = React.useState<string>(savedData?.description || "");
  const [descriptionError, setDescriptionError] = React.useState<string>("");

  const [odontogramaCarie, setOdontogramaCarie] = React.useState<IOdont[]>(savedData?.odontogramaCarie || []);

  const [odontogramaProblem, setOdontogramaProblem] = React.useState<IOdont[]>(
    savedData?.odontogramaProblem || []
  );

  const [odontogramaCirurgy, setOdontogramaCirurgy] = React.useState<IOdont[]>(
    savedData?.odontogramaCirurgy || []
  );

  const [reason, setReason] = React.useState<string>(savedData?.reason || "");
  const [reasonError, setReasonError] = React.useState<string>("");

  const patientActually = localStorage.getItem("personAttendance");

  const patientObj = patientActually ? JSON.parse(patientActually) : null;

  // Salvar dados no localStorage sempre que houver mudança
  React.useEffect(() => {
    const dataToSave = {
      step,
      firstQuestion,
      firstReason,
      secondQuestion,
      care,
      careReason,
      periodont,
      periodontReason,
      cirurgy,
      cirurgyReason,
      description,
      odontogramaCarie,
      odontogramaProblem,
      odontogramaCirurgy,
      reason,
    };
    localStorage.setItem(AVALIATION_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [
    step,
    firstQuestion,
    firstReason,
    secondQuestion,
    care,
    careReason,
    periodont,
    periodontReason,
    cirurgy,
    cirurgyReason,
    description,
    odontogramaCarie,
    odontogramaProblem,
    odontogramaCirurgy,
    reason,
  ]);

  // Função para limpar dados da avaliação
  const clearAvaliationData = React.useCallback(() => {
    localStorage.removeItem(AVALIATION_STORAGE_KEY);
    setStep(1);
    setFirstQuestion("");
    setFirstReason("");
    setSecondQuestion("");
    setCare(false);
    setCareReason("");
    setPeriodont(false);
    setPeriodontReason("");
    setCirurgy(false);
    setCirurgyReason("");
    setDescription("");
    setOdontogramaCarie(() => []);
    setOdontogramaProblem(() => []);
    setOdontogramaCirurgy(() => []);
    setReason("");
  }, []);

  const value = React.useMemo(
    () => ({
      step,
      setStep,
      patientObj,
      firstQuestion,
      setFirstQuestion,
      firstReason,
      setFirstReason,
      secondQuestion,
      setSecondQuestion,
      care,
      setCare,
      careReason,
      setCareReason,
      careReasonError,
      setCareReasonError,
      periodont,
      setPeriodont,
      periodontReason,
      setPeriodontReason,
      periodontReasonError,
      setPeriodontReasonError,
      cirurgy,
      setCirurgy,
      cirurgyReason,
      setCirurgyReason,
      cirurgyReasonError,
      setCirurgyReasonError,
      description,
      setDescription,
      descriptionError,
      setDescriptionError,
      odontogramaCarie,
      setOdontogramaCarie,
      odontogramaProblem,
      setOdontogramaProblem,
      odontogramaCirurgy,
      setOdontogramaCirurgy,
      reason,
      setReason,
      reasonError,
      setReasonError,
      clearAvaliationData,
    }),
    [
      step,
      patientObj,
      firstQuestion,
      firstReason,
      secondQuestion,
      care,
      careReason,
      careReasonError,
      periodont,
      periodontReason,
      periodontReasonError,
      cirurgy,
      cirurgyReason,
      cirurgyReasonError,
      description,
      descriptionError,
      odontogramaCarie,
      odontogramaProblem,
      odontogramaCirurgy,
      reason,
      reasonError,
      clearAvaliationData,
    ]
  );

  return (
    <AvaliationContext.Provider value={value}>
      {children}
    </AvaliationContext.Provider>
  );
};

const useAvaliation = (): IAvaliationContext =>
  React.useContext(AvaliationContext);

export { AvaliationContext, AvaliationProvider, useAvaliation };
